import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { QuoteService } from '../shared/quote.service';
import { NotificationService } from '../shared/notification.service';
import { QuoteDetail, QuoteOption } from '../models/quote';
import { QuoteHeader } from "./quote-header/quote-header";
import { QuoteDetails } from "./quote-details/quote-details";
import { AppModule } from '../app.module';
import { Location } from '@angular/common';
import { ClientService } from '../shared/client.service';
import { PricingTemplate } from '../models/client';
import { AuthService } from '../auth.service';
import { ReportService } from '../shared/report.service';
import { Base64Report } from '../models/report';
import { FileUploader } from '../shared/file-uploader/file-uploader';
import { IncidentService } from '../shared/incident.service';
import { Document, Incident } from '../models/incident';
import { PdfViewer } from "../shared/pdf-viewer/pdf-viewer";
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-quote',
  imports: [QuoteHeader, QuoteDetails, AppModule, FileUploader, PdfViewer],
  templateUrl: './quote.html',
  styleUrl: './quote.less'
})
export class Quote implements OnInit {

  public quote: QuoteDetail;
  public isSaving: boolean = false;
  public isLoading: boolean = true;
  public isLoadingLines: boolean = false;
  public pricingTemplates: PricingTemplate[] = [];

  public isUploadModalVisible = false;

  public incident: Incident = new Incident();
  public invoiceUrl: SafeResourceUrl | null = null;

  public get isReadOnly(): boolean {
    //The logic reads easier if the if statements check if we CAN edit the quote, and return the inverse due to the isReadOnly naming
    return !((this.quote.sellerId === this.authService.user?.clientId && this.quote.statusId === 1)
      || (this.quote.clientId === this.authService.user?.clientId && (this.quote.incidentStatusId === 11 || this.quote.incidentStatusId === 12) && this.quote.statusId === 1));
  }

  public get canSelectOptions(): boolean {
    return (this.quote.clientId === this.authService.user?.clientId && this.quote.statusId === 1 && this.quote.stockChecked !== null);    
  }

  constructor(
    private route: ActivatedRoute, 
    private location: Location,
    private quoteService: QuoteService, 
    private notification: NotificationService,
    private clientService: ClientService,
    public authService: AuthService,
    private reportService: ReportService,
    private router: Router,
    private incidentService: IncidentService,
    private sanitizer: DomSanitizer
  ) {}
    

  ngOnInit() {
    this.route.params.subscribe(params => {
      const quoteId = params['quoteId'];
      // Fetch the quote details using the quoteId
      if (this.quote && this.quote.quoteId === quoteId) {
        // If the quote is already loaded, no need to fetch it again
        return;
      }
      this.quoteService.getQuote(quoteId).subscribe({next: quote => {
        this.quote = quote;

        const searchedIncident = this.incidentService.openIncidents().find(incident => incident.incidentId === quote.incidentId);
        
        //if found, no in list, no need to fetch from server
        if (searchedIncident) {
          console.log("Incident found in list, no need to fetch from server");
          this.incident = searchedIncident;
        } else {
          this.incidentService.getIncident(quote.incidentId).subscribe(incident => {
            console.log("Fetched incident from server:");
            this.incident = incident;
          });
      }

        if (this.quote.basedOnQuoteId) {
          this.clientService.getPricingTemplates(this.authService.user!.clientId).subscribe({next: templates => {
            this.pricingTemplates = templates;
          },
          error: error => {
            this.notification.handleError(error, 'Failed to load pricing templates');
          }});
        }
        this.isLoading = false;
      }, 
      error: error => {
        this.notification.handleError(error, 'Failed to load quote details');
        this.isLoading = false;
      }});
    });
  }

  public onClickBackToIncident() {
    this.router.navigate(['/incident', this.quote.incidentId]);
  }

  saveQuote() {
    this.isSaving = true;
    this.quoteService.updateQuote(this.quote).subscribe({
      next: updatedQuote => {
        //const oldId = this.quote.quoteId;
        this.notification.showSuccess('Quote saved successfully');
        const newUrl = window.location.pathname.replace(this.quote.quoteId.toString(), updatedQuote.quoteId.toString());
        this.location.replaceState(newUrl);
        this.quote = updatedQuote;
        this.isSaving = false;
      },
      error: err => {
        this.notification.handleError(err, 'Failed to save quote');
        this.isSaving = false;
      }
    });
  }

  public onClickPreviewPriceTemplateChanges(pricingTemplateId: number) {
    this.isLoadingLines = true;
    this.quoteService.previewQuotePricing(this.quote.quoteId, pricingTemplateId).subscribe({
      next: updatedQuote => {
        this.quote = updatedQuote;
        this.isLoadingLines = false;
      },
      error: err => {
        this.notification.handleError(err, 'Failed to preview pricing template changes');
        this.isLoadingLines = false;
      }
    });
  }

  public onClickQuotePDF(quoteId: number) {
    this.reportService.generateQuotePDF(quoteId).subscribe({
      next: (report: Base64Report) => {
        this.notification.showSuccess('Quote PDF generated successfully.');
        this.downloadFile(report);
      },
      error: () => {
        this.notification.handleError('Failed to generate Quote PDF report.');
      }
    });
  }

  public convertToInvoice() {
    this.isSaving = true;
    this.quoteService.updateQuote(this.quote).subscribe({
      next: updatedQuote => {
        this.quoteService.convertQuoteToInvoice(this.quote.quoteId).subscribe({
          next: response => {
            this.notification.showSuccess(response.detail);
            this.quote.statusId = 11; //set to converted
            this.router.navigate(['/invoice', this.quote.quoteId]);
          },
          error: err => {
            this.notification.handleError(err, 'Failed to convert quote to invoice');
          },
          complete: () => {
            //complete runs with success or error
            this.isSaving = false;
          }
        });
      },
      error: err => {
        this.notification.handleError(err, 'Failed to save quote before converting to invoice');
        this.isSaving = false;
        return;
      }
    });
  }

  public convertToJobCard() {
    this.isSaving = true;
    this.quoteService.updateQuote(this.quote).subscribe({
      next: updatedQuote => {
        this.quote = updatedQuote;
        this.quoteService.convertQuoteToJobcard(this.quote.quoteId).subscribe({
          next: response => {
            this.notification.showSuccess(response.detail);
            this.quote.statusId = 2; //set to jobcard
            
          },
          error: err => {
            this.notification.handleError(err, 'Failed to convert quote to jobcard');
          },
          complete: () => {
            //complete runs with success or error
            this.isSaving = false;
          }
        });
      },
      error: err => {
        this.notification.handleError(err, 'Failed to save quote before converting to jobcard');
        this.isSaving = false;
        return;
      }
    });
    
  }

  private downloadFile(report: Base64Report) {
    const link = document.createElement('a');
    link.href = 'data:' + report.fileType + ';base64,' + report.base64Data;
    link.download = report.fileName;
    link.click();
  }

  public viewInvoice(){
    this.router.navigate(['/invoice', this.quote.quoteId]);
  }

  public onClickUploadSPInvoice() {
    this.isUploadModalVisible = true;
  }

  public onUploadComplete(event: Document[]): void {
      // Add documents to incident, with duplication checking
      const incident =this.incidentService.openIncidents().find(e => e.incidentId === this.quote.incidentId);
      if (incident && event && event.length > 0) {
        event.forEach((newDoc) => {
          const exists = incident.documents.some(
            (doc) => doc.documentId === newDoc.documentId
          );
          if (!exists) {
            incident.documents.push(newDoc);
            
          }
        });
        incident.statusId = 12; //set incident status to Invoice Received
        this.quote.incidentStatusId = 12;
      }
      this.isUploadModalVisible = false;
    }

  public onUploadOK(): void {
    this.isUploadModalVisible = false;
    this.quote.incidentStatusId = 12;
  }

  public toggleSPInvoice() {
    if (this.invoiceUrl) {
      this.invoiceUrl = null;
    } else {
      const externalInvoiceUrl = this.incident?.documents.find(doc => doc.documentClassificationId === 4)?.url;
      if (externalInvoiceUrl && this.isValidUrl(externalInvoiceUrl)) {
        this.invoiceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(externalInvoiceUrl);
      } else {
        this.invoiceUrl = null;
        this.notification.showError('Invalid or missing invoice URL');
      }
    }
  }

  private isValidUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return ['https:', 'http:'].includes(parsedUrl.protocol);
    } catch {
      return false;
    }
  }
}
